import { forwardRef, useState } from 'react';
import { useT } from '../../i18n';
import { fromISO, toISO, today } from './dates';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const DOW = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

interface CalendarProps {
  value: string | null;
  onPick: (iso: string) => void;
}

/**
 * The S2 calendar (custom, not the native wheel): Monday first, future days disabled, the
 * month after this one unreachable. Picking keeps it open: correcting a date shouldn't
 * cost a tap. Remounted (via `key`) to jump to a new value's month.
 */
export const Calendar = forwardRef<HTMLDivElement, CalendarProps>(function Calendar({ value, onPick }, ref) {
  const t = useT();
  const [cursor, setCursor] = useState(() => {
    const d = value ? fromISO(value) : today();
    return { y: d.getFullYear(), m: d.getMonth() };
  });
  const { y, m } = cursor;
  const now = today();
  const startDow = (new Date(y, m, 1).getDay() + 6) % 7;
  const days = new Date(y, m + 1, 0).getDate();
  const nextDisabled = y > now.getFullYear() || (y === now.getFullYear() && m >= now.getMonth());
  const step = (n: number) => {
    const d = new Date(y, m + n, 1);
    setCursor({ y: d.getFullYear(), m: d.getMonth() });
  };

  return (
    <div className="cal" ref={ref} data-testid="calendar">
      <div className="cal__head">
        <button type="button" className="cal__nav" aria-label={t('S2.cal.prev')} onClick={() => step(-1)}>
          ‹
        </button>
        <div className="cal__month" aria-live="polite">
          {MONTHS[m]} {y}
        </div>
        <button
          type="button"
          className="cal__nav"
          aria-label={t('S2.cal.next')}
          disabled={nextDisabled}
          onClick={() => step(1)}
        >
          ›
        </button>
      </div>
      <div className="cal__grid">
        {DOW.map((d, i) => (
          <div key={i} className="cal__dow" aria-hidden="true">
            {d}
          </div>
        ))}
        {Array.from({ length: startDow }, (_, i) => (
          <span key={`e${i}`} className="cal__day is-empty" aria-hidden="true" />
        ))}
        {Array.from({ length: days }, (_, i) => {
          const date = new Date(y, m, i + 1);
          const iso = toISO(date);
          const on = iso === value;
          return (
            <button
              key={iso}
              type="button"
              className={`cal__day${on ? ' is-sel' : ''}`}
              disabled={date > now}
              aria-pressed={on}
              aria-label={`${i + 1} ${MONTHS[m]} ${y}`}
              onClick={() => onPick(iso)}
            >
              {i + 1}
            </button>
          );
        })}
      </div>
    </div>
  );
});
